import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './bandeauAdmin.scss';
import { useAdmin } from '../../contexts/AdminContext';
import { uploadImage, saveCollection } from '../../utils/adminApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faTimes, faSignOutAlt, faCheck, faSpinner, faGauge } from '@fortawesome/free-solid-svg-icons';

function BandeauAdmin() {
  const { isAdmin, logout, token } = useAdmin();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const [contactForm, setContactForm] = useState({
    email: '', telephone: '', adresse: '', facebook: '', instagram: '',
    logoText: null, logo: null,
    logoTextPreview: '', logoPreview: '',
  });

  useEffect(() => {
    if (isContactOpen) {
      fetch('/api/content/contactInfo')
        .then((r) => r.json())
        .then((data) => {
          setContactForm({
            email: data.email || '',
            telephone: data.telephone || '',
            adresse: data.adresse || '',
            facebook: data.facebook || '',
            instagram: data.instagram || '',
            logoText: null,
            logo: null,
            logoTextPreview: data.logoText || '',
            logoPreview: data.logo || '',
          });
        })
        .catch(console.error);
    }
  }, [isContactOpen]);

  if (!isAdmin) return null;

  const formatTelephone = (input) => {
    const digits = input.replace(/\D/g, '').substring(0, 10);
    return digits.replace(/(\d{2})(?=\d)/g, '$1.');
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: name === 'telephone' ? formatTelephone(value) : value,
    }));
  };

  const handleFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setContactForm((prev) => ({
      ...prev,
      [name]: file,
      [`${name}Preview`]: preview,
    }));
  };

  const handleContactSave = async () => {
    setSaveStatus('saving');
    try {
      let logoTextUrl = contactForm.logoTextPreview;
      let logoUrl = contactForm.logoPreview;

      if (contactForm.logoText instanceof File) {
        logoTextUrl = await uploadImage(contactForm.logoText, token);
      }
      if (contactForm.logo instanceof File) {
        logoUrl = await uploadImage(contactForm.logo, token);
      }

      const data = {
        email: contactForm.email,
        telephone: contactForm.telephone,
        adresse: contactForm.adresse,
        facebook: contactForm.facebook,
        instagram: contactForm.instagram,
        logoText: logoTextUrl,
        logo: logoUrl,
      };

      await saveCollection('contactInfo', data, token);
      setSaveStatus('saved');
      setTimeout(() => {
        setSaveStatus(null);
        setIsContactOpen(false);
      }, 1200);
    } catch (err) {
      console.error(err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  return (
    <>
      {/* Bouton flottant */}
      <button
        className="admin-fab"
        onClick={() => setIsPanelOpen(true)}
        aria-label="Ouvrir le panneau admin"
        title="Administration"
      >
        <FontAwesomeIcon icon={faCog} />
      </button>

      {/* Panneau admin */}
      {isPanelOpen && (
        <div className="admin-panel" role="dialog" aria-modal="true">
          <div className="admin-panel__overlay" onClick={() => setIsPanelOpen(false)} />
          <div className="admin-panel__drawer">
            <div className="admin-panel__header">
              <span className="admin-panel__title">
                <FontAwesomeIcon icon={faCog} /> Administration
              </span>
              <button
                className="admin-panel__close"
                onClick={() => setIsPanelOpen(false)}
                aria-label="Fermer"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="admin-panel__body">
              <p className="admin-panel__hint">
                Mode admin actif — accédez au tableau de bord pour modifier le contenu.
              </p>

              <button
                className="admin-panel__action"
                onClick={() => { setIsContactOpen(true); setIsPanelOpen(false); }}
              >
                Informations générales
              </button>

              <Link
                to="/admin/dashboard"
                className="admin-panel__action admin-panel__action--secondary"
                onClick={() => setIsPanelOpen(false)}
              >
                <FontAwesomeIcon icon={faGauge} /> Tableau de bord
              </Link>

              <button
                className="admin-panel__action admin-panel__action--danger"
                onClick={() => { logout(); setIsPanelOpen(false); }}
              >
                <FontAwesomeIcon icon={faSignOutAlt} /> Se déconnecter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale informations générales */}
      {isContactOpen && (
        <div className="admin-modale" role="dialog" aria-modal="true">
          <div className="admin-modale__overlay" onClick={() => setIsContactOpen(false)} />
          <div className="admin-modale__content">
            <div className="admin-modale__header">
              <h2>Informations générales</h2>
              <button onClick={() => setIsContactOpen(false)} className="admin-modale__close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="admin-modale__body">
              <div className="admin-field">
                <label>Email</label>
                <input type="email" name="email" value={contactForm.email} onChange={handleContactChange} />
              </div>
              <div className="admin-field">
                <label>Téléphone</label>
                <input type="tel" name="telephone" value={contactForm.telephone} onChange={handleContactChange} maxLength={14} />
              </div>
              <div className="admin-field">
                <label>Adresse</label>
                <input type="text" name="adresse" value={contactForm.adresse} onChange={handleContactChange} />
              </div>
              <div className="admin-field">
                <label>Facebook</label>
                <input type="url" name="facebook" value={contactForm.facebook} onChange={handleContactChange} />
              </div>
              <div className="admin-field">
                <label>Instagram</label>
                <input type="url" name="instagram" value={contactForm.instagram} onChange={handleContactChange} />
              </div>
              <div className="admin-field">
                <label>Logo avec texte</label>
                {contactForm.logoTextPreview && (
                  <img src={contactForm.logoTextPreview} alt="Logo avec texte" className="admin-field__preview" />
                )}
                <input type="file" name="logoText" accept="image/*" onChange={handleFileChange} />
              </div>
              <div className="admin-field">
                <label>Logo sans texte</label>
                {contactForm.logoPreview && (
                  <img src={contactForm.logoPreview} alt="Logo sans texte" className="admin-field__preview" />
                )}
                <input type="file" name="logo" accept="image/*" onChange={handleFileChange} />
              </div>
            </div>

            <div className="admin-modale__footer">
              <button
                className="admin-modale__save"
                onClick={handleContactSave}
                disabled={saveStatus === 'saving'}
              >
                {saveStatus === 'saving' && <FontAwesomeIcon icon={faSpinner} spin />}
                {saveStatus === 'saved' && <FontAwesomeIcon icon={faCheck} />}
                {saveStatus === 'error' && '❌ Erreur'}
                {!saveStatus && 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BandeauAdmin;
